function setLangeModuleTitle(fallback){const title=window.LANGE_NATIVE_SEO?.title||fallback;if(document.title!==title)document.title=title;}
import { header, footer, bindHeader, PAGE_PATHS, CONCERN_PATH } from "./_lange-catalogue--chrome.js";
import { SHOP_PATH, BUILDER_PATH, DISCOVERY_PATH, HOME_PATH, escapeHtml } from "./_lange-catalogue--set-builder-core.js";
import { mobilePhoto } from "./_lange-catalogue--responsive-images.js";
const kicker = text => `<p class="lpg-kicker">${escapeHtml(text)}</p>`;
function heroBlock({
  eyebrow,
  title,
  lede,
  image,
  imageAlt = ''
}) {
  return `
  <section class="lpg-hero${image ? '' : ' lpg-hero--plain'}">
    <div class="lpg-hero__copy">
      ${kicker(eyebrow)}
      <h1>${title}</h1>
      ${lede ? `<p class="lpg-hero__lede">${lede}</p>` : ''}
    </div>
    ${image ? `<figure class="lpg-hero__visual">${mobilePhoto(image, `alt="${escapeHtml(imageAlt)}" fetchpriority="high" decoding="async"`)}</figure>` : ''}
  </section>`;
}
function prose(blocks) {
  return `<section class="lpg-prose">${blocks}</section>`;
}
function proofPanel({
  eyebrow,
  title,
  copy,
  items,
  tone = 'forest'
}) {
  return `
  <section class="lpg-proof lpg-proof--${escapeHtml(tone)}">
    <div class="lpg-proof__wash" aria-hidden="true"></div>
    <div class="lpg-proof__intro">
      ${kicker(eyebrow)}
      <h2>${title}</h2>
      <p>${escapeHtml(copy)}</p>
    </div>
    <div class="lpg-proof__cards">
      ${items.map((item, index) => `<article>
        <span>${String(index + 1).padStart(2, '0')}</span>
        <h3>${escapeHtml(item.title)}</h3>
        <p>${escapeHtml(item.copy)}</p>
      </article>`).join('')}
    </div>
  </section>`;
}
function accordion(items) {
  return `<div class="lpg-accordion">${items.map((item, index) => `
    <details${index === 0 ? ' open' : ''}>
      <summary><span>${escapeHtml(item.q)}</span><i aria-hidden="true"></i></summary>
      <div>${item.a}</div>
    </details>`).join('')}</div>`;
}
const DRAFT = false;
function needsInput(note, fallback = '') {
  if (!DRAFT) return fallback;
  return `<span class="lpg-todo" title="Replace before publishing">${escapeHtml(note)}</span>`;
}
function needsInputNote(note, fallback = '') {
  const body = needsInput(note, fallback);
  return body ? `<p class="lpg-note">${body}</p>` : '';
}
const MAISON_ARC = [{
  year: '2008',
  title: 'The first formula',
  copy: 'LANGÉ opens with the Algo-Active Miracle Serum, built on a kombucha base with the marine active GP4G.'
}, {
  year: '2012',
  title: 'Lift-Me Day Cream',
  copy: 'A day cream around Liftiss SB, drawn from baobab fruit pulp. In the Maison’s consumer panel, 77% of users reported gains in radiance and firmness — self-assessment, not a clinical trial.'
}, {
  year: '2016',
  title: 'Skin in the city',
  copy: 'Urban Defense Serum and Urban Protect Day Cream answer a question customers had started asking: what city air does to skin.'
}, {
  year: '2020',
  title: 'The Platinium range',
  copy: 'Cellular Re-Densifying Night Cream leads a range built around overnight repair.'
}, {
  year: '2024',
  title: 'Skin Matrix',
  copy: 'The Ultimate Face Contouring Serum introduces ribose and Zizyphus jujuba seed to the collection.'
}, {
  year: '2026',
  title: 'Exosome Longevity',
  copy: 'The Ultimate Rejuvenating Face Serum opens the Maison’s most recent range.'
}];
function heritageArc(items) {
  return `
  <section class="lpg-arc" id="heritage" aria-labelledby="heritage-title">
    <div class="lpg-arc__intro">
      ${kicker('The range, year by year')}
      <h2 id="heritage-title">Eighteen years,<br><em>one address.</em></h2>
      <p>A Maison is easier to judge across time than in a single campaign. Here is what LANGÉ has actually put its name to since 2008, and when.</p>
    </div>
    <ol class="lpg-arc__list">
      ${items.map(item => `<li>
        <span class="lpg-arc__year">${escapeHtml(item.year)}</span>
        <h3>${escapeHtml(item.title)}</h3>
        <p>${escapeHtml(item.copy)}</p>
      </li>`).join('')}
    </ol>
  </section>`;
}
function maisonPage() {
  return heroBlock({
    eyebrow: 'The Maison',
    title: 'One place behind<br><em>every jar.</em>',
    lede: 'Since 2008, LANGÉ has formulated, filled and checked its skincare in Saint-Jean-de-Braye, near Orléans. That gives you a clearer answer to a simple question: what is this formula, and why is it here?',
    image: '/lange/paris/editorial/formulation-hero-v2-1600.webp',
    imageAlt: 'Formulation at the LANGÉ site'
  }) + `
  <section class="lpg-pillars">
    <article><strong>2008</strong><span>Formulating in France since</span></article>
    <article><strong>1</strong><span>Site for formulation, filling and control</span></article>
    <article><strong>35</strong><span>Face-care formulas in the collection</span></article>
    <article><strong>Full INCI</strong><span>Published on every product page</span></article>
  </section>

  ${prose(`
    <h2>What happens at the Maison</h2>
    <p>A skincare label can tell you where a jar was filled. It does not always tell you who built the formula, who chose the texture, or who checked the batch. Those steps matter because they shape what touches your skin every day.</p>
    <p>At LANGÉ, formulation, filling and quality control happen at our own Saint-Jean-de-Braye site. The people working on a texture are close to the people who make it. If a batch needs attention, it stays with the team who knows the formula.</p>
    <p>For you, that means the information on a product page is not a sales summary passed between suppliers. It is a description of a formula made here, with its ingredients shown in full.</p>
  `)}

  ${proofPanel({
    eyebrow: 'What you can check before you buy',
    title: 'A premium claim should be easy to inspect.',
    copy: 'The useful proof is not a vague promise. It is the information you can find quickly on the page in front of you.',
    items: [{
      title: 'The complete formula',
      copy: 'Every product page lists its INCI ingredients, in the order used on pack.'
    }, {
      title: 'The role in a routine',
      copy: 'You can see whether a product is made for morning, evening, The Eye area, or a treatment step.'
    }, {
      title: 'What to expect',
      copy: 'We describe texture, use and consumer feedback without presenting skincare as medicine.'
    }]
  })}

  <section class="lpg-split" id="provenance">
    <figure><img src="/lange/paris/editorial/formulation-worktable-v1-1600.webp" alt="The formulation worktable" loading="lazy" decoding="async"></figure>
    <div>
      ${kicker('Where it comes from')}
      <h2>The Cosmetic Valley,<br><em>close to home.</em></h2>
      <p>Saint-Jean-de-Braye sits near Orléans, in the French region known as the Cosmetic Valley. It brings together laboratories, packaging specialists, regulatory expertise and cosmetic manufacturing in one area.</p>
      <p>That proximity makes work easier to see and easier to question. Formula development, production and control are not spread across a chain of distant companies. They happen at one address, with one team responsible for the finished product.</p>
      <p class="lpg-note">Our active ingredients are mostly of natural origin. They are chosen for their place in the formula, their stability and how the finished texture feels on skin — not because a single ingredient sounds impressive on the front of a jar.</p>
    </div>
  </section>

  ${heritageArc(MAISON_ARC)}

  ${prose(`
    <h2>Who started it</h2>
    <p>LANGÉ was founded in 2008 by Eric Fustier. The first product to carry the name, the Algo-Active Miracle Serum, set the pattern the range still follows: an active chosen for what it does inside a formula rather than for how it sounds on a label, made at the Maison’s own site.${needsInput(' [NEEDS INPUT: Eric Fustier’s background before 2008 — training, previous work, why he started the Maison. One paragraph would carry this section.]', '')}</p>
    <p>Eighteen years on, that is still the question every product page here tries to answer, one formula at a time.</p>
  `)}

  <section class="lpg-split lpg-split--reverse" id="international">
    <figure><img src="/lange/generated/lange-spa-heritage-hq.webp" alt="A LANGÉ spa entrance in China" loading="lazy" decoding="async"></figure>
    <div>
      ${kicker('French expertise abroad')}
      <h2>Ten spas,<br><em>three hundred trained.</em></h2>
      <p>LANGÉ’s official Maison history records the opening of ten LANGÉ spas in China, supported by more than 300 beauticians trained in the Maison’s methods. Alongside them, the range reaches other countries through specialist distributors, general retail and pharmacies.</p>
      <p>The number worth pausing on is the second one. Sending jars abroad is straightforward. Teaching 300 people to use a formula the way it was designed to be used is slower, and it is the part that says most about how the Maison works.</p>
      <div class="lpg-stats">
        <div><strong>10</strong><span>LANGÉ spas in China</span></div>
        <div><strong>200+</strong><span>Beauticians trained</span></div>
      </div>
    </div>
  </section>

  <section class="lpg-split lpg-split--reverse">
    <figure><img src="/lange/paris/editorial/actives-texture-1100.webp" alt="Active ingredients" loading="lazy" decoding="async"></figure>
    <div>
      ${kicker('How we keep claims clear')}
      <h2>Clear claims,<br><em>confident choices.</em></h2>
      <p>Skincare can help skin feel more comfortable, look smoother and sit better through the day. It is not a replacement for medical care, and a consumer panel is not a clinical study. We keep those distinctions visible.</p>
      <p>When a formula has consumer feedback, it is labelled as self-assessment. When the evidence cannot support a strong claim, we do not turn it into one. This is less dramatic than miracle language, but it makes choosing easier.</p>
      <a class="lpg-link" href="${SHOP_PATH}">See how each formula is explained <span aria-hidden="true">→</span></a>
    </div>
  </section>

  ${ctaBand('Start with the Discovery Set', 'Two full-size Carat formulas and a 7 ml face cream for €39, so you can try the line before committing to it.', DISCOVERY_PATH, 'See the Discovery Set')}`;
}
function madeInFrancePage() {
  return `
  <section class="lmf-hero">
    <img class="lmf-hero__bg" src="/lange/source/brand-ritual.webp" alt="The LANGÉ site at Saint-Jean-de-Braye in the Cosmetic Valley" fetchpriority="high" decoding="async">
    <div class="lmf-hero__copy">
      <p class="lmf-kicker">Saint-Jean-de-Braye · Orléans · France</p>
      <h1>Made in France<br><em>means made here.</em></h1>
      <p>LANGÉ has been formulating, filling and quality controlling its face care at one French site since 2014. Here is what that covers, and why it matters when you choose a formula.</p>
    </div>
    <div class="lmf-hero__meta"><span>Cosmetic Valley, France</span><span>Quality · Safety · Effectiveness</span></div>
  </section>

  <section class="lmf-stats">
    <article><strong>2014</strong><span>Formulating in the Cosmetic Valley since</span></article>
    <article><strong>1</strong><span>French site for the full process</span></article>
    <article><strong>65</strong><span>Face-care formulas</span></article>
    <article><strong>Full INCI</strong><span>On every product page</span></article>
  </section>

  ${prose(`
    <h2>What our “made in France” covers</h2>
    <p>Those words should tell you more than where a box was packed. For LANGÉ, they cover the formula work, filling and quality control at our site near Orléans. It is one process, carried out by one French team.</p>
    <p>This does not mean every ingredient grows in France; cosmetic formulas use materials from different places. It does mean the formula is assembled, made and checked here, and that the full ingredient list is published so you can decide for yourself.</p>
    <p>We choose to keep the work together because a texture that is not right should return to the bench, not be explained away in marketing. It keeps the process slower, closer and easier to account for.</p>
  `)}

  ${proofPanel({
    eyebrow: 'The practical benefit',
    title: 'Closer work, clearer answers.',
    copy: '“Made in France” is useful only if it changes what you can know about what you are buying.',
    items: [{
      title: 'One accountable team',
      copy: 'The formula and the finished product stay with the same site from development to release.'
    }, {
      title: 'A traceable page',
      copy: 'The product page gives you the full INCI list, texture, use and the formula’s place in a routine.'
    }, {
      title: 'A direct route for questions',
      copy: 'Customer care can take a real question about a product back to the people responsible for it.'
    }],
    tone: 'stone'
  })}

  <section class="lmf-steps">
    <div class="lmf-steps__head">
      ${kicker('How a product is made')}
      <h2>Three stages,<br><em>one address.</em></h2>
      <p>Every formula passes through all three in the same building. The chemist adjusting a texture is a short walk from the line that fills it.</p>
    </div>
    <div class="lmf-steps__grid">
      <article>
        <figure><img src="/lange/paris/editorial/formulation-worktable-v1-1600.webp" alt="Formulating French skincare at the LANGÉ worktable" loading="lazy" decoding="async"></figure>
        <span>01</span>
        <h3>Formulation</h3>
        <p>Our team builds and revises the textures at the site. Small changes matter: a cream has to spread comfortably, sit well in a routine and feel good enough to use again tomorrow.</p>
      </article>
      <article>
        <figure><img src="/lange/generated/french-lab.jpg" alt="Filling LANGÉ skincare on our own line in France" loading="lazy" decoding="async"></figure>
        <span>02</span>
        <h3>Filling</h3>
        <p>Products are filled here in the packs selected for each formula. Keeping this step on site keeps the link between the formula and the finished jar clear.</p>
      </article>
      <article>
        <figure><img src="/lange/paris/editorial/formulation-hero-v2-1600.webp" alt="Quality control on French-made skincare" loading="lazy" decoding="async"></figure>
        <span>03</span>
        <h3>Control</h3>
        <p>Checks take place before a batch is released. If something needs attention, the people checking it can speak directly with the people who made it.</p>
      </article>
    </div>
  </section>

  <section class="lmf-quote">
    <img class="lmf-quote__bg" src="/lange/paris/campaign/formulation-film-1600.webp" alt="" aria-hidden="true" loading="lazy" decoding="async">
    <blockquote>
      <p>What is in the jar<br><em>should never be a mystery.</em></p>
    </blockquote>
  </section>

  <section class="lpg-split">
    <figure><img src="/lange/generated/botanical-texture.jpg" alt="Natural ingredients used in LANGÉ French skincare" loading="lazy" decoding="async"></figure>
    <div>
      ${kicker('Ingredients')}
      <h2>Mostly natural,<br><em>fully listed.</em></h2>
      <p>Many of the actives in our formulas come from plants, ferments, algae and botanical oils. Other ingredients are used because they help a formula stay stable, spread well or remain comfortable on skin.</p>
      <p>“Natural” is not a promise that a formula suits everyone. Skin can react to natural materials as well as synthetic ones. The useful thing is to publish the full list and explain the formula plainly, so you can check it against your own needs.</p>
      <p>That is why every product page carries its complete ingredient list in the same order as the pack. If you know an ingredient does not work for you, you should be able to find it before you buy.</p>
      <a class="lpg-link" href="${SHOP_PATH}">See any product page <span aria-hidden="true">→</span></a>
    </div>
  </section>

  <section class="lmf-why">
    <div class="lmf-why__head">
      ${kicker('The Cosmetic Valley')}
      <h2>Why this part<br><em>of France.</em></h2>
      <p>The region around Chartres and Orléans has long been a centre for cosmetic expertise. The reason is practical: laboratories, production knowledge, regulation and suppliers are close enough to work together.</p>
    </div>
    <div class="lmf-why__grid">
      <article>
        <h3>The expertise is local</h3>
        <p>Laboratories, regulatory specialists, pack makers and raw-material suppliers work across the region. That makes it possible to build and check a formula without creating distance between the people responsible for it.</p>
      </article>
      <article>
        <h3>Short distances</h3>
        <p>Working from one site makes product decisions easier to follow. It does not make a formula simple, but it avoids turning every question into a chain of handovers.</p>
      </article>
      <article>
        <h3>It shows in the writing</h3>
        <p>When we explain a product, we start with what it is for, how it feels and how to use it. We keep medical promises and exaggerated results out of the conversation.</p>
      </article>
    </div>
  </section>

  ${ctaBand('See what we make here', 'Sixty-five French skincare formulas, every ingredient published in full.', SHOP_PATH, 'Browse the collection')}`;
}
function sciencePage() {
  return heroBlock({
    eyebrow: 'The Science',
    title: 'The useful part<br><em>of the formula.</em>',
    lede: 'Ingredients, texture, use and consumer feedback should help you make a better choice. This is how we separate useful information from a long list of claims.',
    image: '/lange/paris/editorial/formula-texture-1400.webp',
    imageAlt: 'Formula texture'
  }) + `
  ${prose(`
    <h2>What we look for in a formula</h2>
    <p>A formula has to do more than sound good on a label. It needs a texture people want to apply, natural ingredients that make sense together, and a clear place in a routine. If it feels too heavy, pills under make-up or leaves skin uncomfortable, it has missed the point.</p>
    <p>We also ask a simple writing question: can we describe the formula honestly? A cream can support skin comfort and a smoother-looking finish. It cannot diagnose, treat or erase a skin condition. Those lines matter.</p>

    <h2>How to read consumer feedback</h2>
    <p>Product pages include results from consumer use testing. Those figures report what the people in that panel said after using the formula. They are useful when you want to know how a texture felt or whether skin looked more rested to the person using it.</p>
    <p>They are not a clinical measurement of wrinkle depth, and we do not describe them as one. Where testing information appears, it is labelled as consumer feedback.</p>

    <h2>Natural origin, without the shortcut</h2>
    <p>LANGÉ uses mostly natural-origin actives, including plant extracts, ferments, roots, algae and botanical oils. We also use other ingredients when they help the formula remain stable, safe for the skin and comfortable to use.</p>
    <p>Natural is not the same as gentle for every person. Long INCI names are not automatically a problem either. The useful decision is personal: read the full list, consider known sensitivities, and introduce a new product carefully.</p>
  `)}

  ${proofPanel({
    eyebrow: 'The product-page standard',
    title: 'Less guesswork at the point of choice.',
    copy: 'Every formula should make the next decision easier: is this for me, how would I use it, and what can I reasonably expect?',
    items: [{
      title: 'Ingredient list',
      copy: 'The complete INCI is shown in the same order as the product pack.'
    }, {
      title: 'Texture and routine',
      copy: 'Each page explains where the product fits and the kind of finish it is made to give.'
    }, {
      title: 'Clear limits',
      copy: 'Consumer feedback is presented as feedback, not turned into a clinical promise.'
    }]
  })}

  ${ctaBand('Read a formula before choosing it', 'Start with the complete ingredient list, how to use the product and what it is designed to support.', SHOP_PATH, 'Browse the collection')}`;
}
const FAQ_ITEMS = [{
  q: 'How much is delivery, and how long does it take?',
  a: `<p>Delivery is free throughout Europe on every order, with no minimum. Orders are dispatched from France.${needsInput(' Delivery times are [NEEDS INPUT: working days for France / EU / rest of world]', '')}</p>`
}, {
  q: 'Can I return something?',
  a: `<p>We offer 30-day returns from delivery. Items must be sent back to us. Full detail is on the <a href="${PAGE_PATHS.delivery}">Delivery &amp; returns</a> page.</p>`
}, {
  q: 'How do the sets work?',
  a: `<p>Any two formulas are €89 and any three are €119, whichever you choose — the price depends on how many you take, never on which. The full-size creams alone are €118–€128 each, so a set of three is a substantial reduction. Build one on the <a href="${BUILDER_PATH}">set builder</a>.</p>`
}, {
  q: 'Is the Discovery Set different?',
  a: `<p>Yes. The <a href="${DISCOVERY_PATH}">Discovery Set</a> is a fixed €39 Carat introduction: a 30 ml Serum, a 20 ml Eye treatment and a 7 ml face cream. The set builder is for choosing your own full-size formulas.</p>`
}, {
  q: 'Which formula should I start with?',
  a: `<p>If lines and loss of firmness are what you notice, start at <a href="${CONCERN_PATH}/fine-lines/">fine lines &amp; firmness</a>. If it shows around the eyes first, start at <a href="${CONCERN_PATH}/eye-area/">The Eye area</a>. If you would rather be asked, the ritual finder on the home page takes six questions.</p>`
}, {
  q: 'Are the products suitable for sensitive skin?',
  a: `<p>The formulas are fragranced, so if you react to perfume in skincare they are not the place to start. Every full ingredient list is published on its product page — worth reading first if you have a known allergy.</p>`
}, ...(DRAFT ? [{
  q: 'Are they tested on animals?',
  a: `<p>${needsInput('[NEEDS INPUT: animal testing policy. Cosmetics testing on animals is banned in the EU, but state your position explicitly — customers look for it.]')}</p>`
}] : []), {
  q: 'Do you offer samples?',
  a: `<p>Complimentary samples are included with every order.</p>`
}, {
  q: 'Is there a subscription?',
  a: `<p>No. There is no subscription, no auto-renewal and no card kept on file for a later charge. When you want something again, you order it again.</p>`
}];
function faqPage() {
  return heroBlock({
    eyebrow: 'Customer care',
    title: 'Questions,<br><em>answered plainly.</em>',
    lede: 'If what you need is not here, write to us at info@langeparis.com and a person will reply.'
  }) + `<section class="lpg-faq">${accordion(FAQ_ITEMS)}</section>
  ${ctaBand('Still unsure?', 'Write to us. Customer care answers on working days.', PAGE_PATHS.contact, 'Contact the Maison')}`;
}
function deliveryPage() {
  return heroBlock({
    eyebrow: 'Customer care',
    title: 'Delivery<br><em>&amp; returns.</em>',
    lede: 'What it costs, how long it takes, and what happens if you change your mind.'
  }) + prose(`
    <h2>Delivery</h2>
    <p>Delivery is <strong>free throughout Europe</strong>, on every order, with no minimum spend. Every order also includes complimentary samples.</p>
    <p>Orders are dispatched from our site in France.${needsInput(' Dispatch takes [NEEDS INPUT: dispatch time] and delivery takes [NEEDS INPUT: delivery windows by destination]', ' If you need an order by a particular date, write to us before you order and we will tell you what is possible.')}</p>
    ${needsInput('<p>Outside Europe, [NEEDS INPUT: do you ship worldwide? Who pays duties?]</p>', '<p>For destinations outside Europe, write to <a href="mailto:info@langeparis.com">info@langeparis.com</a> before ordering and we will confirm whether we can deliver to you and what it would cost.</p>')}

    <h2>Returns</h2>
    <p><strong>30-day returns.</strong> You can return your items within 30 days of delivery.</p>
    <p>Items must be sent back to us to receive a refund.</p>
    <p>To start a return, write to <a href="mailto:info@langeparis.com">info@langeparis.com</a> with your order number and what you would like to send back, and we will confirm the return address and the next step.</p>
    <p>${needsInput('Return postage is [NEEDS INPUT: who pays return postage?] ', 'We will confirm the return address and who covers return postage when you write to us. ')}Refunds are issued to the original payment method once the item reaches us and has been checked${needsInput(', normally within [NEEDS INPUT: refund processing time]', '')}.</p>

    <h2>If something arrives damaged or wrong</h2>
    <p>That is not a return, it is our mistake. Write to <a href="mailto:info@langeparis.com">info@langeparis.com</a> within 14 days with your order number and a photograph, and we will replace it or refund it in full. You will not be asked to pay postage.</p>

    <h2>Your statutory rights</h2>
    <p>Nothing here limits the rights you have under EU consumer law, including the right of withdrawal on distance purchases. Where our policy and your statutory rights differ, your statutory rights apply.</p>
    ${needsInputNote('[NEEDS INPUT: Have the 30-day returns policy reviewed before publishing, including return postage and refund processing times.]', '')}
  `);
}
function contactPage() {
  const topics = ['An order I have placed', 'A return or an exchange', 'Which formula suits my skin', 'Press, wholesale or partnerships', 'Something else'];
  const quickAnswers = [{
    href: PAGE_PATHS.delivery,
    label: 'Delivery & returns',
    copy: 'Free throughout Europe, and exactly what happens if you change your mind.'
  }, {
    href: PAGE_PATHS.faq,
    label: 'Frequently asked questions',
    copy: 'Sets, samples, sensitive skin, subscriptions — the things we are asked most.'
  }, {
    href: CONCERN_PATH + '/fine-lines',
    label: 'Where to start',
    copy: 'If lines and firmness are what you notice, begin here rather than with an email.'
  }, {
    href: BUILDER_PATH,
    label: 'Build your set',
    copy: 'Any two formulas for €89, any three for €119, whichever you choose.'
  }];
  return heroBlock({
    eyebrow: 'Contact',
    title: 'Ask a real<br><em>question.</em>',
    lede: 'Need help with an order, a return or choosing a formula? Write to the Maison. Your message goes to a person, not a chatbot or an automated recommendation.',
    image: '/lange/paris/editorial/portrait-limestone-1600.webp',
    imageAlt: 'A LANGÉ portrait in daylight'
  }) + `
  <section class="lpg-contact">
    <div class="lpg-contact__main">
      ${kicker('Write to us')}
      <h2>Tell us what is<br><em>getting in the way.</em></h2>
      <p class="lpg-contact__intro">An order number, the product name, or a simple description of how your skin feels gives us a better place to start. We will answer with the information you need, not a copied script.</p>

      <form class="lpg-form" data-contact-form novalidate>
        <div class="lpg-field">
          <label for="lpg-name">Your name</label>
          <input id="lpg-name" name="name" type="text" autocomplete="name" placeholder="Camille Roux">
        </div>
        <div class="lpg-field">
          <label for="lpg-email">Your email</label>
          <input id="lpg-email" name="email" type="email" autocomplete="email" placeholder="you@example.com">
        </div>
        <div class="lpg-field">
          <label for="lpg-topic">What is it about?</label>
          <div class="lpg-select">
            <select id="lpg-topic" name="topic">${topics.map(topic => `<option>${escapeHtml(topic)}</option>`).join('')}</select>
            <i aria-hidden="true"></i>
          </div>
        </div>
        <div class="lpg-field">
          <label for="lpg-order">Order number <span>optional</span></label>
          <input id="lpg-order" name="order" type="text" placeholder="LP-00000">
        </div>
        <div class="lpg-field lpg-field--wide">
          <label for="lpg-message">Your message</label>
          <textarea id="lpg-message" name="message" rows="6" placeholder="Tell us as much as you can."></textarea>
        </div>
        <p class="lpg-form__error" role="alert" data-contact-error></p>
        <div class="lpg-form__foot">
          <button type="submit">Send your message <span aria-hidden="true">→</span></button>
          <p class="lpg-form__note">This opens an email with your message filled in, so you can review it before sending and keep a copy. You can also write directly to <a href="mailto:info@langeparis.com">info@langeparis.com</a>.</p>
        </div>
      </form>
    </div>

    <aside class="lpg-contact__aside">
      <div class="lpg-card">
        ${kicker('Customer care')}
        <p class="lpg-card__lede">Orders, returns and straightforward help with choosing a formula.</p>
        <a class="lpg-contact__link" href="mailto:info@langeparis.com">info@langeparis.com</a>
        <dl class="lpg-card__facts">
          <div><dt>Answering</dt><dd>Working days, 08:00–17:00 CET</dd></div>
          <div><dt>For urgent order issues</dt><dd>Please include your order number</dd></div>
          <div><dt>Written from</dt><dd>Saint-Jean-de-Braye, France</dd></div>
        </dl>
      </div>
      <div class="lpg-card lpg-card--quiet">
        ${kicker('Press & wholesale')}
        <p class="lpg-card__lede">For press enquiries, stockists and partnerships — the same address, marked for the Maison.</p>
        <a class="lpg-contact__link" href="mailto:info@langeparis.com?subject=Press%20and%20wholesale%20enquiry">info@langeparis.com</a>
      </div>
    </aside>
  </section>

  ${proofPanel({
    eyebrow: 'What happens next',
    title: 'A simple route to an answer.',
    copy: 'Contact should reduce uncertainty, not create another task. Here is the quickest way to get the right help.',
    items: [{
      title: 'Choose a topic',
      copy: 'Tell us whether this is about an order, a return, your skin or something else.'
    }, {
      title: 'Add the useful detail',
      copy: 'For an order, include its number. For skincare, tell us what you are trying to solve and what you already use.'
    }, {
      title: 'Review before sending',
      copy: 'The form opens your own email, so you can see exactly what leaves your inbox.'
    }],
    tone: 'stone'
  })}

  <section class="lpg-quick">
    <div class="lpg-quick__head">
      ${kicker('Before you write')}
      <h2>Get the answer<br><em>without waiting.</em></h2>
      <p>If one of these is your question, this page will be faster than an email.</p>
    </div>
    <div class="lpg-quick__grid">
      ${quickAnswers.map((item, index) => `
        <a class="lpg-quick__card" href="${item.href}">
          <span>${String(index + 1).padStart(2, '0')}</span>
          <strong>${escapeHtml(item.label)}</strong>
          <p>${escapeHtml(item.copy)}</p>
          <i aria-hidden="true">→</i>
        </a>`).join('')}
    </div>
  </section>

  <section class="lpg-split lpg-split--reverse">
    <figure><img src="/lange/paris/editorial/formulation-worktable-v1-1600.webp" alt="The formulation worktable at the LANGÉ site" loading="lazy" decoding="async"></figure>
    <div>
      ${kicker('Where we are')}
      <h2>Saint-Jean-de-Braye,<br><em>near Orléans.</em></h2>
      <p>Everything we sell is formulated and made at one site in the Cosmetic Valley, the cluster around Chartres and Orléans that produces a significant share of French cosmetics. Formulation, filling and quality control sit under one roof, which is why a question about a batch reaches someone who can go and look at it.</p>
      <p>It is a working laboratory and production floor rather than a shop, so it is not open to visitors. Everything reaches us by email.</p>
      ${needsInputNote('[NEEDS INPUT: full postal address and company registration number — both are expected on a European commerce site, and this is the natural place for them]', '')}
      <a class="lpg-link" href="${PAGE_PATHS.madeInFrance}">How and where it is made <span aria-hidden="true">→</span></a>
    </div>
  </section>
  ` + ctaBand('Not sure what to ask yet?', 'The Discovery Set is a €39 introduction to Carat: a 30 ml serum, a 20 ml eye treatment and a 7 ml face cream.', DISCOVERY_PATH, 'See the Discovery Set');
}
function privacyPage() {
  return heroBlock({
    eyebrow: 'Legal',
    title: 'Privacy<br><em>policy.</em>',
    lede: 'What this site collects, why, and how to make us stop.'
  }) + prose(`

    <h2>Analytics, and your choice</h2>
    <p>Nothing is measured until you say yes. When you first arrive you are asked whether we may collect anonymous analytics; if you decline, no analytics are loaded. You can change your mind at any time from the privacy control on any page.</p>
    <p>Where you consent, we collect anonymous usage data — pages viewed, where visitors get stuck, how the site performs on mobile. Session recordings mask form inputs.</p>
    ${needsInput('<p>The processors used for this are [NEEDS INPUT: name the analytics providers and where they store data]</p>', '')}

    <h2>What stays in your browser</h2>
    <p>The set you build is stored in your own browser using local storage. It never reaches us, it is not linked to you, and clearing your browser data removes it. It exists so your selection survives moving between pages.</p>

    <h2>If you write to us</h2>
    <p>When you email <a href="mailto:info@langeparis.com">info@langeparis.com</a>, we keep the correspondence so we can deal with your enquiry and any follow-up. We do not add you to a mailing list because you contacted customer care.</p>

    <h2>The newsletter</h2>
    <p>If you sign up, we keep your email address to send what you signed up for. Every message carries an unsubscribe link, and unsubscribing removes you.</p>
    ${needsInput('<p>The provider used to send it is [NEEDS INPUT: email service provider]</p>', '')}

    <h2>Maison Welcome Wheel</h2>
    <p>If you enter the Maison Welcome Wheel, we use your email address to administer one entry, prevent duplicates, send a reward code or contact a physical-prize winner. Marketing email is separate and optional: we only send it when you tick the marketing box. The rules and published odds are on the <a href="${PAGE_PATHS.terms}#maison-wheel-prize-draw">prize draw rules</a>.</p>

    <h2>Your rights</h2>
    <p>Under the GDPR you may ask what we hold about you, ask for it to be corrected or deleted, object to processing, or ask for a copy in a portable form. Write to <a href="mailto:info@langeparis.com">info@langeparis.com</a> and we will respond within one month.</p>
    <p>The data controller is LANGÉ INTERNATIONAL SAS, with its registered office at 25, rue de Ponthieu, 75008 Paris, France, registered under number 941 431 512. VAT number: FR62941431512. If you believe your data has been handled improperly you may complain to the CNIL, the French data protection authority.</p>
  `);
}
function termsPage() {
  return heroBlock({
    eyebrow: 'Legal',
    title: 'General Terms &amp; Conditions<br><em>of Sale.</em>',
    lede: 'Effective September 1, 2026.'
  }) + prose("<h2>1 – Purpose and acceptance of terms and conditions</h2>\n<p>LANGÉ Paris website (hereinafter the « Site » ) is an e-commerce website accessible via the Internet at www.langeparis.com. It is open to any user of this network (hereinafter referred to as « User »). It is edited by the company LANGÉ INTERNATIONAL SAS (hereinafter referred to as « Seller » ), a simplified joint stock company with a capital of 40,000 Euros, whose registered office is located at 25, rue de Ponthieu – 75008 Paris – France, registered with the Trade and Companies.</p>\n<p>Registry under number 941 431 512 ( hereinafter referred to as « Seller » ).</p>\n<p>Publication Director: Eric FUSTIER</p>\n<p>VAT number :FR62941431512</p>\n<p>It is hosted by OVH which is headquartered :</p>\n<p>2 rue Kellermann – 59100 Roubaix – France</p>\n<p>The website allows Langé to offer for sale cosmetics to Internet users browsing the webSite (hereinafter referred to as « Users »). For the purposes hereof, it is agreed that the User and Langé will be collectively referred to as the « Parties » and individually as « Party » , and the user who validated an order will be referred to as « Buyer ». The rights and obligations of the User apply to Buyer. Ordering product offered on the Site requires consultation and express acceptance of these terms and conditions, but this acceptance is conditioned by a handwritten signature from the user.</p>\n<p>It is noted that the user can save or print the present general conditions of sale, provided that they do not change.</p>\n<p>These general conditions are valid from September 1, 2026. Langé reserves the right to modify these General and Specific Terms and Conditions at any time without notice, provided that such changes will not apply to bookings and orders previously accepted and confirmed by the Buyer.</p>\n<p>These conditions apply to the exclusion of any other document.</p>\n<h2>2 – Products marketed www.langeparis.com</h2>\n<p>All offers available on the Site are limited to mainland Europe. Buyer may , prior to his order, take note on the Site Langeparis , essential characteristics of the products they wish to order.</p>\n<p>The bid including promotional offers are valid until they are announced on the</p>\n<p>website.</p>\n<h2>3 – Prices of products sold on the site</h2>\n<p>The prices listed on this Site are in Euros including all taxes and are subject to change during the year , provided that the products ordered are charged at prevailing prices at the time of the order.</p>\n<p>They do not include shipping costs, charged in addition to the price of products purchased according to the amount of the order. Shipping charges are shown before the registration of the order by the Buyer.</p>\n<p>Prices include value added tax (VAT) applicable on the date of the order and any change in the applicable rate will be reflected on the price of products sold by Langé in the Site.</p>\n<h2>4 – Controls</h2>\n<p>Any order implies acceptance, without limitation or qualification these terms and conditions of sale.</p>\n<p>Buyer agrees that the registration of the order of Langé International systems as proof of the nature of the agreement and the date.</p>\n<p>The amount of each order is limited to the amount of EUR 1000 per order. In addition, the same reference product can not be ordered for an amount greater than 25 items per order.</p>\n<p>When the order is confirmed by clicking the « Complete Order » button , the Buyer hereby accepts it and these terms and conditions of sale. The order can be printed.</p>\n<p>Upon registration of the order, a detailed receipt thereof will be sent to the Buyer at the email address that will be shown.</p>\n<p>This acknowledgment will specify the exact amount charged and the terms of delivery of the order.</p>\n<p>This acknowledgment is accepting the order and validate the transaction subject to the payment of the order.</p>\n<p>In case of unavailability of one or more products after placing the order, you will be notified by e -mail. The amount of your order will be recalculated and you will be charged the new rate, minus the missing products. If your order is completely unavailable you will be notified by email and you will not be charged.</p>\n<p>You can also cancel an order placed on the website by contacting customer service at info@langeparis.com. If customer service informs you that the order has been processed, you cannot cancel it then you should refuse the package upon receipt of your order.</p>\n<p>Langé, however, reserves the right to suspend or cancel any order from a buyer with whom there is a dispute over payment of a previous order, or for any other legitimate reason including taking the unusual nature of the order.</p>\n<h2>5 – Payments</h2>\n<p>All orders are payable in Euros.</p>\n<p>To set the order, the Buyer has the following payment method: credit card.</p>\n<p>Credit cards accepted on site are: Visa, Master Card and American Express.</p>\n<p>Payments via PayPal are also accepted.</p>\n<p>Buyer guarantees that Langé has the required permission to use the payment method he chose, when saving the order.</p>\n<p>Langé reserves the right to suspend or cancel any order and / or delivery, whatever its nature and level of execution in case of non- payment of any sum due by the Buyer or in the case of payment incident.</p>\n<p>Penalties in an amount equal to the legal interest rate plus five points are automatically applicable to amounts outstanding at the end of a period of ten days from the date of invoice.</p>\n<p>In the context of the fight against Internet fraud, information about your order may be sent to any competent authority for verification.</p>\n<p>To ensure the security of credit card payment, the Purchaser shall send to the Paypal security code (CVV ) of his credit card.</p>\n<p>PayPal supports payments, refunds and releases flows.</p>\n<p>Payment security on the site is provided by Paypal.</p>\n<h2>6 – Delivery of orders</h2>\n<p>The products will be delivered to the address specified by the Buyer on the order by French Post for orders under or egal to 119 Euros and by express (UPS, DHL,TNT,Fedex) for orders above 119 Euros.</p>\n<p>Delivery will be made in an average of 2 to 5 days from receipt of order, depending on the shipping method selected and a maximum within 30 days of receipt of order.</p>\n<h2>7 – Legal warranty</h2>\n<p>The buyer benefits from the provisions of the legal guarantee of conformity and the legal guarantee against hidden defects.</p>\n<p>In case of lack of conformity of the product, in particular due to a defect or error in the references supplied , the Purchaser may elect to return the product to Langé and obtain a refund.</p>\n<p>Return costs will be reimbursed by Langé at the request of the Buyer.</p>\n<p>For any claim , the Purchaser may contact Langé at the following address : info@langeparis.com</p>\n<p>You have a period of two years from the date of delivery of the Product to take action under the legal guarantee of conformity. In this case, you may choose between repairing or replacing the Product, subject to the cost conditions set out in Article L217-9 of the Consumer Code.</p>\n<p>For contracts concluded after 01 September 2026, You are exempted from proving the existence of the Product’s lack of conformity during the twenty-four (24) months following the delivery of the Product. For contracts concluded previously, the defect is presumed for six (6) months.</p>\n<p>You are reminded that the legal guarantee of conformity applies independently of any commercial guarantee that may be granted to you by LANGÉ INTERNATIONAL SAS.</p>\n<p>In addition, You may decide to invoke the guarantee against hidden defects of the item sold within the meaning of Article 1641 of the Civil Code. In this case, You may choose between rescission of the sale or a reduction in the purchase price in accordance with Article 1644 of the Civil Code.</p>\n<h2>8 – Right of withdrawal</h2>\n<p>The Purchaser shall have a period of 14 days from the date of receipt to return at its expense, the products ordered to refund.</p>\n<p>Reimbursement will be made by any means of payment chosen by the Buyer expressed after exercising his right of withdrawal, no later than 30 days after the date on which the right is exercised.</p>\n<h2>9 – Instructions for returning products</h2>\n<p>Whatever the reason for returning the products to Langé, it is advisable to Buyer follow these instructions in order to facilitate the processing of said return:</p>\n<p>1. Use the original packaging to return the product.</p>\n<p>2. Complete and attach the Returns to detach from the Delivery.</p>\n<p>3. Send the package to the following address:</p>\n<p>e-shop Langé Returns</p>\n<p>25, rue de Ponthieu</p>\n<p>75008 Paris</p>\n<p>FRANCE</p>\n<h2>10 – Retention of title</h2>\n<p>Langé retains full ownership of the goods sold until full payment is received , in principal, including fees and taxes.</p>\n<h2>11 – Responsibility</h2>\n<p>Langé will not be held liable for breach of contract in case of out of stock except in cases of non- promotion price for disposal of stocks, or in case of unavailability of the product due to a case of force majeure.</p>\n<p>Lancior disclaims all responsibility:</p>\n<p>– For any site interruption;</p>\n<p>– For any bugs;</p>\n<p>– For any damage resulting from fraudulent intrusion by a third party leading to a modification of the information available on the site;</p>\n<p>– And more generally for any direct or indirect damage, whatever the cause, origin , nature or consequences, including without limitation any loss of profits , goodwill, data or other intangible losses that may result from anyone accessing the Site or inability to access or reliance upon any information coming directly or indirectly from it.</p>\n<h2>12 – Intellectual Property</h2>\n<p>In accordance with laws governing literary and artistic property rights or other similar rights, this site and all the elements, trademarks, designs, models, photographs, text, graphics, logos, animated or not , with or without sound, graphics etc.. located on this site and their compilation are the exclusive property of Langé, it does not grant any license or right other than to consult the site.</p>\n<p>Reproduction or use of all or part of these elements is only authorized for the purposes of information for personal and private use , any reproduction and any use of copies made for other purposes is expressly prohibited. Neither the Site (or any part), nor its content, nor the marks may not be used, reproduced , duplicated, copied, sold , resold, accessed, modified, or otherwise exploited , in whole or in part for any purpose whatsoever without the prior written permission of Lancior.</p>\n<p>Any other use constitutes infringement.</p>\n<h2>13 – Applicable Law</h2>\n<p>Sales of Langé products are subject to French law.</p>\n<h2>14 – Computer and Freedom</h2>\n<p>The information collected is subject to a data processing for sending a newsletter and / or management control of the Buyer.</p>\n<p>The recipients of the data are Langé and its contractors.</p>\n<p>The Buyer has a right to access, rectify, oppose and delete data relating to Lancior by sending a email to info@langeparis.com</p>\n<p>The site has been declared to CNIL under registration process.</p>");
}
function ctaBand(title, copy, href, label) {
  const isDiscovery = href === DISCOVERY_PATH;
  return `
  <section class="lpg-cta${isDiscovery ? ' lpg-cta--discovery' : ''}">
    ${isDiscovery ? `<figure class="lpg-cta__photo">
      <img src="/lange/paris/campaign/carat-discovery-set-detail-7ml-1536.jpg"
        alt="The Carat Discovery Set: 30 ml Serum, 20 ml Eye treatment and 7 ml face cream"
        width="1536" height="1024" loading="lazy" decoding="async">
    </figure>` : ''}
    <div>
      <h2>${escapeHtml(title)}</h2>
      <p>${escapeHtml(copy)}</p>
    </div>
    <a href="${href}">${escapeHtml(label)} <span aria-hidden="true">→</span></a>
  </section>`;
}
export const SEO = {
  maison: {
    title: 'The Maison — French skincare made in our own laboratory since 2008',
    description: 'LANGÉ is a French skincare maison that formulates, fills and checks every product at its own site in the Cosmetic Valley. Mostly natural ingredients, every list published.'
  },
  madeInFrance: {
    title: 'Made in France — French skincare from our own laboratory',
    description: 'Our French skincare is developed, made and checked at one site near Orléans in the Cosmetic Valley. Mostly natural ingredients, no outside laboratory, full ingredient lists on every formula.'
  },
  science: {
    title: 'The Science — how our French skincare is formulated and tested',
    description: 'How LANGÉ judges a formula: consumer testing and its limits, natural-origin actives, and the complete ingredient list published on all 65 French skincare formulas.'
  },
  faq: {
    title: 'Frequently asked questions',
    description: 'Delivery, returns, how the sets work, sensitivity and ingredients — answered plainly by LANGÉ Paris.'
  },
  delivery: {
    title: 'Delivery & returns',
    description: 'Free delivery throughout Europe on every LANGÉ order, complimentary samples, and 30-day returns.'
  },
  contact: {
    title: 'Contact the Maison',
    description: 'Contact LANGÉ Paris about an order, a return, or which French skincare formula suits your skin. A person replies.'
  },
  privacy: {
    title: 'Privacy policy',
    description: 'How LANGÉ Paris handles your data.'
  },
  terms: {
    title: 'Terms of service',
    description: 'The terms on which LANGÉ Paris sells.'
  }
};
const routeKey = pathname => pathname.replace(/\/+$/, '');
const ROUTES = {
  [routeKey(PAGE_PATHS.maison)]: {
    build: maisonPage,
    seo: SEO.maison,
    nav: 'maison'
  },
  [routeKey(PAGE_PATHS.madeInFrance)]: {
    build: madeInFrancePage,
    seo: SEO.madeInFrance,
    nav: 'maison'
  },
  [routeKey(PAGE_PATHS.science)]: {
    build: sciencePage,
    seo: SEO.science,
    nav: 'maison'
  },
  [routeKey(PAGE_PATHS.faq)]: {
    build: faqPage,
    seo: SEO.faq,
    nav: ''
  },
  [routeKey(PAGE_PATHS.delivery)]: {
    build: deliveryPage,
    seo: SEO.delivery,
    nav: ''
  },
  [routeKey(PAGE_PATHS.contact)]: {
    build: contactPage,
    seo: SEO.contact,
    nav: ''
  },
  [routeKey(PAGE_PATHS.privacy)]: {
    build: privacyPage,
    seo: SEO.privacy,
    nav: ''
  },
  [routeKey(PAGE_PATHS.terms)]: {
    build: termsPage,
    seo: SEO.terms,
    nav: ''
  }
};
export function renderRoute(pathname) {
  const route = ROUTES[routeKey(pathname)];
  if (!route) return null;
  return {
    seo: route.seo,
    html: `<div class="lc-site lpg-site">${header({
      current: route.nav
    })}<main>${window.LANGE_PAGE_CONTENT || route.build()}</main>${footer()}</div>`
  };
}
function bindContactForm() {}
function focusHashTarget() {
  const id = decodeURIComponent(location.hash.slice(1));
  if (!id) return;
  const jump = () => {
    const target = document.getElementById(id);
    if (target) target.scrollIntoView({
      block: 'start',
      behavior: 'instant'
    });
  };
  jump();
  if (document.readyState === 'complete') return;
  let readerTookOver = false;
  const release = () => {
    readerTookOver = true;
  };
  window.addEventListener('wheel', release, {
    once: true,
    passive: true
  });
  window.addEventListener('touchstart', release, {
    once: true,
    passive: true
  });
  window.addEventListener('keydown', release, {
    once: true
  });
  window.addEventListener('load', () => {
    if (!readerTookOver) jump();
  }, {
    once: true
  });
}
function start() {
  const app = document.getElementById('root');
  if (app) app.hidden = true;
  let target = document.getElementById('lange-catalogue-root');
  if (!target) {
    target = document.createElement('div');
    target.id = 'lange-catalogue-root';
    document.body.append(target);
  }
  const key = routeKey(window.LANGE_ROUTE);
  const route = ROUTES[key];
  document.documentElement.lang = 'en';
  if (!route) {
    target.innerHTML = window.langeResolve(`<div class="lc-site lpg-site">${header()}<main><section class="lpg-hero lpg-hero--plain"><div class="lpg-hero__copy"><h1>Page not found</h1><p class="lpg-hero__lede">That page is not part of the site.</p></div></section></main>${footer()}</div>`);
    bindHeader();
    return;
  }
  const title = `${route.seo.title} | LANGÉ PARIS`;
  setLangeModuleTitle(title);
  target.innerHTML = window.langeResolve(`<div class="lc-site lpg-site">${header({
    current: route.nav
  })}<main>${window.LANGE_PAGE_CONTENT || route.build()}</main>${footer()}</div>`);
  window.setTimeout(() => {
    setLangeModuleTitle(title);
  }, 650);
  bindHeader();
  bindContactForm();
  focusHashTarget();
}
if (typeof document !== 'undefined') start();